import React, { useEffect } from 'react';
import { Button, Flex, Form, Switch, Tag } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import collect from 'collect.js';
import { useTranslation } from 'react-i18next';
import useHelpers from '../../hooks/context/useHelpers';
import CurrencyInput from '../form/general/CurrencyInput';

export default function PriceEditor({ data, update, selectedPrice, disableTiers, className }) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Subscription:PriceEditor' });

  const { formatCurrency } = useHelpers();

  const addTier = (item) => update('tiers', [...data.tiers, item ?? {}]);
  const removeTier = (index) => update('tiers', collect(data.tiers).forget(index).toArray());
  const updateTier = (index, field, value) =>
    update(
      'tiers',
      data.tiers.map((item, i) =>
        index === i
          ? {
              ...item,
              [field]: value,
            }
          : item,
      ),
    );

  useEffect(() => {
    if (!data.has_pricing_tiers) {
      return;
    }

    data.tiers.length === 0 &&
      update('tiers', [
        {
          threshold: 0,
          amount: 35,
        },
        {
          threshold: 700,
          amount: data.amount,
        },
      ]);

    data.tiers.length !== 0 && updateTier(data.tiers.length - 1, 'amount', data.amount);
  }, [data.has_pricing_tiers, data.amount]);

  return (
    <div className={className}>
      <Form layout="vertical" initialValues={data}>
        <Form.Item
          required
          name="amount"
          label={t('form.amount.label')}
          rules={[
            { required: true, message: t('form.amount.validation.required') },
            {
              validator: (_, value) =>
                value >= 0
                  ? Promise.resolve()
                  : Promise.reject(new Error(t('form.amount.validation.min', { value: 0 }))),
            },
          ]}
        >
          <CurrencyInput
            value={data.amount}
            onChange={(value) => update('amount', value)}
            disabled={selectedPrice}
            className="!w-fit"
          />
        </Form.Item>
      </Form>
      {!disableTiers && (
        <>
          <Form.Item>
            <Flex>
              <Switch
                checked={data.has_pricing_tiers}
                disabled={selectedPrice}
                onChange={(value) => update('has_pricing_tiers', value)}
                id="has_pricing_tiers"
              />
              <label htmlFor="has_pricing_tiers" className="ml-xs">
                {t('form.has_pricing_tiers.label')}
              </label>
            </Flex>
          </Form.Item>
          {data.has_pricing_tiers && (
            <div>
              <hr className="my-md" />
              <div className="grid grid-cols-2 gap-4">
                <h6 className="mb-0">{t('form.threshold.label')}</h6>
                <h6 className="mb-0">{t('form.amount.label')}</h6>
                {data.tiers.map((item, index) => (
                  <React.Fragment key={`pricing-tier-${index}`}>
                    <div className="flex">
                      {index === data.tiers.length - 1 && (
                        <Tag className="mr-xs my-auto" color="success">
                          {'>='}
                        </Tag>
                      )}
                      <CurrencyInput
                        id={`tier-${index}-threshold`}
                        value={item.threshold ?? ''}
                        disabled={index === 0}
                        onChange={(value) => updateTier(index, 'threshold', value)}
                      />
                      {index !== data.tiers.length - 1 && (
                        <Tag className="ml-xs mr-0 my-auto text-sm" color={index !== 0 ? 'warning' : 'red'}>
                          {` - ${formatCurrency(data.tiers[index + 1].threshold)}`}
                        </Tag>
                      )}
                    </div>
                    <div className="flex">
                      <CurrencyInput
                        id={`tier-${index}-amount`}
                        value={item.amount ?? ''}
                        disabled={index === data.tiers.length - 1}
                        onChange={(value) => updateTier(index, 'amount', value)}
                      />
                      <Button
                        danger
                        disabled={index === 0 || index === data.tiers.length - 1}
                        onClick={() => removeTier(index)}
                        className="ml-xs my-auto aspect-square flex p-0"
                      >
                        <DeleteOutlined className="m-auto" />
                      </Button>
                    </div>
                  </React.Fragment>
                ))}
              </div>
              <div className="mt-xs flex justify-end">
                <Button onClick={() => addTier(data.tiers[data.tiers.length - 1])}>{t('buttons.add_tier')}</Button>
              </div>
              <hr className="my-md" />
            </div>
          )}
        </>
      )}
    </div>
  );
}
